
import { DueDiligenceFormStages, DueDiligenceStageOrder, StageToKey, StageToSchema } from '@/store/dueDiligence/constants';
import { DueDiligenceFormStage, DueDiligenceFormStageKey } from '@/store/dueDiligence/types';
import { CPButton } from '@capital-platforms/cpui';
import Vue from 'vue';
import { Component, InjectReactive, Prop } from 'vue-property-decorator';
import CPOpenAccountFormProse from '@/components/open-account/form/CPOpenAccountFormProse.vue';
import { formatValidationMessages } from '@/validation';
import { DueDiligenceEmailType } from '@/utils/emailTypes';

@Component({
    components: {
        CPButton,
        CPOpenAccountFormProse
    }
})
export default class CPDueDiligenceStageContainer extends Vue {
    @Prop({ required: true, default: DueDiligenceFormStages.GeneralDetails })
    public readonly stage!: DueDiligenceFormStage;

    @InjectReactive()
    public readonly viewOnly!: boolean;

    get currentStage(): DueDiligenceFormStage {
        return this.$store.state.dueDiligence.formStage;
    }

    get isFirstStage(): boolean {
        return this.$store.getters['dueDiligence/isFirstStage'];
    }

    get isLastStage(): boolean {
        return this.$store.getters['dueDiligence/isLastStage'];
    }

    get isLoading(): boolean {
        return this.$store.state.dueDiligence.isLoading;
    }

    public async handleSave(): Promise<void> {
        if (!this.validateBeforeContinue()) {
            return;
        }
        if (!this.isLastStage) {
            this.$store.commit('dueDiligence/toggleFormEditable', false)
        }
        await this.handleGoNext();
    }

    public handlePrevious(): void {
        this.$store.commit('dueDiligence/toggleFormEditable', false)
        this.$store.dispatch('dueDiligence/goPrev');
    }

    public handleYes(): void {
        this.$store.commit('dueDiligence/toggleFormEditable', true);
    }

    public async handleNo(): Promise<void> {
        if (!this.validateBeforeContinue()) {
            return;
        }

        await this.handleGoNext();
    }

    // On the last stage (final submit) every page is validated, not just the current one.
    private validateBeforeContinue(): boolean {
        return this.isLastStage ? this.validateAllStages() : this.validateCurrentStage();
    }

    private validateAllStages(): boolean {
        for (const stage of DueDiligenceStageOrder) {
            if (!this.validateStage(stage)) {
                if (stage !== this.currentStage) {
                    this.$store.commit('dueDiligence/toggleFormEditable', true);
                    this.$store.commit('dueDiligence/setFormStage', stage);
                }
                return false;
            }
        }

        return true;
    }

    private validateCurrentStage(): boolean {
        return this.validateStage(this.stage);
    }

    private validateStage(stage: DueDiligenceFormStage): boolean {
        const stageKey: DueDiligenceFormStageKey = StageToKey[stage]
        const data = this.$store.state.dueDiligence.details[stageKey];
        const schema = stage === DueDiligenceFormStages.GeneralDetails
            ? StageToSchema[stage](this.$store.state.dueDiligence.fiscalResidenceChanged)
            : StageToSchema[stage]();
        const accountType =
            this.$store.state.dueDiligence.details.GeneralDetails
                ?.PlatformAccount?.PlatformAccountType;

        const validationResult = schema.validate(data, {
            abortEarly: false,
            context: {
                accountType
            }
        });
        
        if (validationResult.error) {
            const errorMessages = formatValidationMessages(validationResult.error.details);
            this.$store.commit('addError', errorMessages[0]);
            return false;
        }

        return true;
    }

    private async handleGoNext(): Promise<void> {
        if (this.isLastStage) {
            const clientBostonId = this.$store.state.dueDiligence.clientBostonId ?? this.$store.state.account.details.bostonId;

            const state = this.$store.state.dueDiligence.details;

            const normalizeReason = (holder: any) => {
                const taxInfo = holder?.TaxInfo;
                if (!taxInfo) return;

                if (typeof taxInfo.ReasonNoTinNumber === "object") {
                    taxInfo.ReasonNoTinNumber = taxInfo.ReasonNoTinNumber?.value ?? null;
                }
            };

            normalizeReason(state?.GeneralDetails?.PrimaryAccountHolder);
            normalizeReason(state?.GeneralDetails?.SecondaryAccountHolder);

            const request = { ...this.$store.state.dueDiligence.details, ConsultantBostonId: Number(this.$store.state.account.details.bostonId), ClientBostonId: Number(clientBostonId), EmailType: DueDiligenceEmailType.ClientSubmissionAdjusted };

            await this.$store.dispatch('dueDiligence/updateUserDetails',
                request);
            await this.$store.dispatch('dueDiligence/getClients');
            return;
        }

        this.$store.dispatch('dueDiligence/goNext');
    }
}
